import supabase from '../utils/supabase.js';

/**
 * Comprehensive Presence Service for VA Sprouts
 * Handles real-time online status tracking with intelligent activity detection
 */
class PresenceService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.heartbeatInterval = null;
    this.presenceSubscription = null;
    this.isOnline = false;
    this.awayTimeout = null;
    this.activityDebounce = null;
    this.presenceCache = new Map();
    this.userId = null;
    
    // Configuration
    this.config = {
      heartbeatInterval: 30000, // 30 seconds
      awayDelay: 60000, // 1 minute
      activityDebounceDelay: 5000, // 5 seconds
      cacheTimeout: 30000, // 30 seconds
      offlineThreshold: 2 * 60 * 1000, // 2 minutes
      awayThreshold: 10 * 60 * 1000 // 10 minutes
    };
    
    // Bind methods to maintain context
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleUserActivity = this.handleUserActivity.bind(this);
    
    this.setupEventListeners();
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize presence tracking for current user
   * @param {string} userId - User ID to track presence for
   * @returns {Promise<Object>} - Result object
   */
  async initialize(userId) {
    if (!userId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PresenceService] No user ID provided');
      }
      return { success: false, error: 'No user ID provided' };
    }

    try {
      // Check authentication status first
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      
      if (authError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] Authentication error:', authError.message);
        }
        return { success: false, error: `Authentication error: ${authError.message}` };
      }
      
      if (!session || !session.user) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] No valid authentication session');
        }
        return { success: false, error: 'No valid authentication session' };
      }
      
      if (session.user.id !== userId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] User ID mismatch with session');
        }
        return { success: false, error: 'User ID mismatch with current session' };
      }

      this.userId = userId;
      
      // Ensure presence record exists
      const ensureResult = await this.ensureUserPresenceRecord(userId);
      if (!ensureResult.success) {
        // Check if the error is due to missing user profile or auth issues
        if (ensureResult.needsProfile) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[PresenceService] User profile does not exist yet. Presence tracking will be enabled after profile creation.');
          }
          return { 
            success: false, 
            error: 'User profile does not exist yet. Presence tracking will be enabled after profile creation.',
            needsProfile: true
          };
        }
        
        // Check for authentication-related errors
        if (ensureResult.error && (
          ensureResult.error.includes('new row violates row-level security') ||
          ensureResult.error.includes('JWT') ||
          ensureResult.error.includes('401') ||
          ensureResult.error.includes('permission denied')
        )) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[PresenceService] Authentication/RLS error, user may not be verified:', ensureResult.error);
          }
          return { 
            success: false, 
            error: 'Authentication required. Please ensure you are logged in and verified.',
            authRequired: true
          };
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] Failed to ensure presence record, continuing anyway:', ensureResult.error);
        }
      } else {
        // Only log presence record creation in development
        if (process.env.NODE_ENV === 'development') {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[PresenceService] Presence record ensured:', ensureResult.action);
          }
        }
      }
      
      // Set initial online status
      const statusResult = await this.setOnlineStatus('online');
      if (!statusResult.success) {
        // Check for auth-related errors in status setting
        if (statusResult.error && (
          statusResult.error.includes('new row violates row-level security') ||
          statusResult.error.includes('JWT') ||
          statusResult.error.includes('401') ||
          statusResult.error.includes('permission denied')
        )) {
          return { 
            success: false, 
            error: 'Authentication required. Please ensure you are logged in and verified.',
            authRequired: true
          };
        }
        throw new Error(statusResult.error);
      }
      
      // Start heartbeat
      this.startHeartbeat();
      
      // Subscribe to presence changes
      this.subscribeToPresenceChanges();
      
      return { success: true };
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceService] Initialization failed:', error);
      }
      
      // Check if this is an auth-related error
      if (error.message && (
        error.message.includes('new row violates row-level security') ||
        error.message.includes('JWT') ||
        error.message.includes('401') ||
        error.message.includes('permission denied')
      )) {
        return { 
          success: false, 
          error: 'Authentication required. Please ensure you are logged in and verified.',
          authRequired: true
        };
      }
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Set user's online status
   * @param {string} status - Status to set ('online', 'away', 'busy', 'offline')
   * @returns {Promise<Object>} - Result object
   */
  async setOnlineStatus(status = 'online') {
    if (!this.userId) {
      return { success: false, error: 'Not initialized - no user ID' };
    }

    try {
      // Verify authentication before attempting to set status
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      
      if (authError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] Authentication error in setOnlineStatus:', authError.message);
        }
        return { success: false, error: `Authentication error: ${authError.message}` };
      }
      
      if (!session || !session.user || session.user.id !== this.userId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] Invalid session in setOnlineStatus');
        }
        return { success: false, error: 'Invalid authentication session' };
      }

      const now = new Date().toISOString();
      const presenceData = {
        user_id: this.userId,
        status,
        last_seen: now,
        last_activity: now,
        session_id: this.sessionId,
        is_active: status === 'online',
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          timestamp: Date.now(),
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          }
        }
      };

      const { data, error } = await supabase
        .from('user_presence')
        .upsert(presenceData, {
          onConflict: 'user_id'
        });

      if (error) {
        // Provide specific error handling for common RLS issues
        if (error.message && error.message.includes('new row violates row-level security')) {
          return { 
            success: false, 
            error: 'Permission denied: User not authenticated or verified for presence tracking' 
          };
        }
        throw error;
      }

      this.isOnline = (status === 'online');
      this.updateLocalCache(this.userId, status, presenceData);
      
      return { success: true, data };
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceService] Failed to set online status:', error);
      }
      return { success: false, error: error.message };
    }
  }

  /**
   * Ensure user has presence record (create if missing)
   * @param {string} userId - User ID to ensure presence for
   * @returns {Promise<Object>} - Operation result
   */
  async ensureUserPresenceRecord(userId) {
    if (!userId) {
      return { success: false, error: 'No user ID provided' };
    }

    try {
      // First check if user profile exists (required by RLS policy)
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, is_verified')
        .eq('id', userId)
        .single();

      if (profileError || !userProfile) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[PresenceService] User profile does not exist for user:', userId);
        }
        return { 
          success: false, 
          error: 'User profile does not exist. RLS policy requires user profile to exist before creating presence record.',
          needsProfile: true
        };
      }

      // Note: We allow presence tracking for all users, regardless of verification status
      // This enables basic presence functionality while users complete verification

      // Check if presence record exists
      const { data: existing } = await supabase
        .from('user_presence')
        .select('user_id')
        .eq('user_id', userId)
        .single();

      if (existing) {
        return { success: true, action: 'already_exists' };
      }

      // Create new presence record
      const { data, error } = await supabase
        .from('user_presence')
        .insert({
          user_id: userId,
          status: 'offline',
          last_seen: new Date().toISOString(),
          last_activity: new Date().toISOString(),
          is_active: false
        })
        .select()
        .single();

      if (error) {
        // Handle specific RLS errors
        if (error.message && error.message.includes('new row violates row-level security')) {
          return {
            success: false,
            error: 'Permission denied: Unable to create presence record. User may not be properly authenticated.',
            authRequired: true
          };
        }
        throw error;
      }

      return { success: true, action: 'created', data };
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceService] Error ensuring presence record:', error);
      }
      
      // Check for authentication-related errors
      if (error.message && (
        error.message.includes('JWT') ||
        error.message.includes('401') ||
        error.message.includes('permission denied')
      )) {
        return {
          success: false,
          error: 'Authentication error: Unable to verify user credentials.',
          authRequired: true
        };
      }
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's current presence status
   * @param {string} userId - User ID to get presence for
   * @returns {Promise<Object>} - Presence result
   */
  async getUserPresence(userId) {
    if (!userId) {
      return { success: false, error: 'No user ID provided' };
    }

    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.debug('[PresenceService] Getting presence for user:', userId);
    }

    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PresenceService] No authenticated session found');
      }
      // Return offline status instead of error for unauthenticated requests
      return { 
        success: true, 
        presence: { 
          user_id: userId,
          status: 'offline', 
          last_seen: null, 
          last_activity: null,
          is_active: false 
        }, 
        source: 'no_auth' 
      };
    }

    // Check cache first
    const cached = this.presenceCache.get(userId);
    if (cached && (Date.now() - cached.timestamp) < this.config.cacheTimeout) {
      // Only log cache hits in development
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[PresenceService] Returning cached presence for user:', userId, cached.data.status);
        }
      }
      return { success: true, presence: cached.data, source: 'cache' };
    }

    try {
      // Use a more explicit select syntax and avoid spaces in column names
      const { data, error } = await supabase
        .from('user_presence')
        .select('user_id,status,last_seen,last_activity,is_active')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found, try to create it automatically for authenticated users
          if (session?.user?.id === userId) {
            if (process.env.NODE_ENV === 'development') {
            console.log('[PresenceService] No presence record found, creating one for user:', userId);
          }
            const createResult = await this.ensureUserPresenceRecord(userId);
            if (createResult.success) {
              // Retry the query after creating the record
              const { data: retryData } = await supabase
                .from('user_presence')
                .select('user_id,status,last_seen,last_activity,is_active')
                .eq('user_id', userId)
                .single();
              
              if (retryData) {
                const actualStatus = this.calculateActualStatus(retryData);
                const updatedPresence = { ...retryData, status: actualStatus };
                this.updateLocalCache(userId, actualStatus, updatedPresence);
                return { success: true, presence: updatedPresence, source: 'created' };
              }
            }
          }
          // Fall through to return default offline status
        } else {
          // Log detailed error information for debugging
          if (process.env.NODE_ENV === 'development') {
            console.error('[PresenceService] Database error details:', {
              error: error.message,
              code: error.code,
              details: error.details,
              hint: error.hint,
              userId,
              authenticatedUser: session?.user?.id
            });
          }
          
          // Handle 406 specifically - try to create presence record
          if (error.code === '406' || error.message?.includes('Not Acceptable')) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[PresenceService] 406 Not Acceptable - attempting to create presence record');
            }
            
            // Try to create presence record for this user
            try {
              const createResult = await this.ensureUserPresenceRecord(userId);
              if (createResult.success) {
                // Retry the query after creating the record
                const { data: retryData, error: retryError } = await supabase
                  .from('user_presence')
                  .select('user_id,status,last_seen,last_activity,is_active')
                  .eq('user_id', userId)
                  .single();
                
                if (!retryError && retryData) {
                  const actualStatus = this.calculateActualStatus(retryData);
                  const updatedPresence = { ...retryData, status: actualStatus };
                  this.updateLocalCache(userId, actualStatus, updatedPresence);
                  return { success: true, presence: updatedPresence, source: 'created_after_406' };
                }
              }
            } catch (createError) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[PresenceService] Failed to auto-create presence record:', createError);
              }
            }
          } else {
            throw error;
          }
        }
      }

      const presence = data || { 
        user_id: userId,
        status: 'offline', 
        last_seen: null, 
        last_activity: null,
        is_active: false 
      };

      // Only log in development mode
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[PresenceService] Raw presence data from DB:', presence);
        }
      }

      // Calculate actual status based on last activity
      const actualStatus = this.calculateActualStatus(presence);
      const updatedPresence = { ...presence, status: actualStatus };
      
      // Only log calculated status in development
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[PresenceService] Calculated status for user:', userId, actualStatus);
        }
      }
      
      this.updateLocalCache(userId, actualStatus, updatedPresence);
      
      return { 
        success: true, 
        presence: updatedPresence, 
        source: 'database' 
      };
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[PresenceService] Failed to get user presence:', error);
      }
      
      // Always return a fallback offline status instead of error
      return { 
        success: true, 
        presence: { 
          user_id: userId,
          status: 'offline', 
          last_seen: null, 
          last_activity: null,
          is_active: false 
        }, 
        source: 'error_fallback' 
      };
    }
  }

  /**
   * Get multiple users' presence status efficiently
   * @param {Array<string>} userIds - Array of user IDs
   * @returns {Promise<Object>} - Presence results
   */
  async getMultipleUserPresence(userIds) {
    if (!userIds || userIds.length === 0) {
      return { success: true, presences: {} };
    }

    try {
      // Check cache first
      const presences = {};
      const uncachedUserIds = [];
      
      userIds.forEach(userId => {
        const cached = this.presenceCache.get(userId);
        if (cached && (Date.now() - cached.timestamp) < this.config.cacheTimeout) {
          presences[userId] = cached.data;
        } else {
          uncachedUserIds.push(userId);
        }
      });

      // Fetch uncached users from database
      if (uncachedUserIds.length > 0) {
        const { data, error } = await supabase
          .from('user_presence')
          .select('user_id,status,last_seen,last_activity,is_active')
          .in('user_id', uncachedUserIds);

        if (error) {
          throw error;
        }

        // Process fetched data
        const fetchedPresences = new Map();
        if (data) {
          data.forEach(presence => {
            const actualStatus = this.calculateActualStatus(presence);
            const updatedPresence = { ...presence, status: actualStatus };
            fetchedPresences.set(presence.user_id, updatedPresence);
            this.updateLocalCache(presence.user_id, actualStatus, updatedPresence);
          });
        }

        // Add fetched presences and default offline for missing users
        uncachedUserIds.forEach(userId => {
          if (fetchedPresences.has(userId)) {
            presences[userId] = fetchedPresences.get(userId);
          } else {
            // User not found in database - default to offline
            const defaultPresence = {
              user_id: userId,
              status: 'offline',
              last_seen: null,
              last_activity: null,
              is_active: false
            };
            presences[userId] = defaultPresence;
            this.updateLocalCache(userId, 'offline', defaultPresence);
          }
        });
      }

      return { success: true, presences };
      
    } catch (error) {
      console.error('[PresenceService] Failed to get multiple user presence:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate actual status based on last activity
   * @param {Object} presence - Presence data from database
   * @returns {string} - Calculated status
   */
  calculateActualStatus(presence) {
    if (!presence || !presence.last_activity) {
      return 'offline';
    }

    const lastActivity = new Date(presence.last_activity);
    const now = new Date();
    const timeSinceActivity = now - lastActivity;

    // Only log status calculation in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.debug('[PresenceService] Status calculation for user:', presence.user_id, {
        lastActivity: presence.last_activity,
        timeSinceActivity: Math.round(timeSinceActivity / 1000) + 's',
        currentStatus: presence.status
      });
    }

    // If user is marked as active and recently updated, they should be online
    if (presence.is_active && timeSinceActivity < this.config.offlineThreshold) {
      return presence.status === 'busy' ? 'busy' : 'online';
    }

    // If user explicitly set status and it's recent, respect it
    if (presence.status === 'busy' && timeSinceActivity < this.config.awayThreshold) {
      return 'busy';
    }

    // Calculate status based on activity with more lenient thresholds
    if (timeSinceActivity < this.config.offlineThreshold) {
      return presence.status === 'away' ? 'away' : 'online';
    } else if (timeSinceActivity < this.config.awayThreshold) {
      return 'away';
    } else {
      return 'offline';
    }
  }

  /**
   * Start heartbeat to maintain online status
   */
  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(async () => {
      if (this.isOnline && this.userId) {
        await this.updateActivity();
      }
    }, this.config.heartbeatInterval);
    

  }

  /**
   * Update user activity timestamp
   */
  async updateActivity() {
    if (!this.userId) return;

    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('user_presence')
        .update({ 
          last_activity: now,
          last_seen: now
        })
        .eq('user_id', this.userId);

      if (error) {
        console.error('[PresenceService] Failed to update activity:', error);
      }
      
    } catch (error) {
      console.error('[PresenceService] Activity update error:', error);
    }
  }

  /**
   * Subscribe to real-time presence changes
   */
  subscribeToPresenceChanges() {
    if (this.presenceSubscription) {
      supabase.removeChannel(this.presenceSubscription);
    }

    this.presenceSubscription = supabase
      .channel('user_presence_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_presence'
      }, (payload) => {
        this.handlePresenceChange(payload);
      })
      .subscribe((_status) => {
        // Subscription status callback - not used but required by API
      });
  }

  /**
   * Handle real-time presence changes
   * @param {Object} payload - Supabase real-time payload
   */
  handlePresenceChange(payload) {
    // Only log realtime events in development mode
    if (process.env.NODE_ENV === 'development') {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[PresenceService] Real-time presence change received:', payload);
      }
    }
    
    try {
      const { eventType, new: newRecord, old: oldRecord } = payload;
      
      if (eventType === 'INSERT' || eventType === 'UPDATE') {
        if (newRecord) {
          // Calculate actual status
          const actualStatus = this.calculateActualStatus(newRecord);
          const updatedRecord = { ...newRecord, status: actualStatus };
          
          // Update local cache
          this.updateLocalCache(newRecord.user_id, actualStatus, updatedRecord);
          
          // Emit custom event for React components
          if (typeof window !== 'undefined' && window.CustomEvent) {
            window.dispatchEvent(new window.CustomEvent('presence-changed', {
              detail: {
                userId: newRecord.user_id,
                status: actualStatus,
                presence: updatedRecord,
                eventType
              }
            }));
          }
        }
      } else if (eventType === 'DELETE' && oldRecord) {
        // User presence deleted - set to offline
        this.updateLocalCache(oldRecord.user_id, 'offline', oldRecord);
        
        // Emit custom event for React components
        if (typeof window !== 'undefined' && window.CustomEvent) {
          window.dispatchEvent(new window.CustomEvent('presence-changed', {
            detail: {
              userId: oldRecord.user_id,
              status: 'offline',
              presence: oldRecord,
              eventType
            }
          }));
        }
      }
      
    } catch (error) {
      console.error('[PresenceService] Error handling presence change:', error);
    }
  }

  /**
   * Setup browser event listeners
   */
  setupEventListeners() {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Handle page unload
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
    // Handle user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, this.handleUserActivity, { passive: true });
    });
    

  }

  /**
   * Handle page visibility changes
   */
  async handleVisibilityChange() {
    if (!this.userId) return;

    if (document.hidden) {
      // Page is hidden - set to away after delay
      this.awayTimeout = setTimeout(async () => {
        if (this.isOnline) {
          await this.setOnlineStatus('away');

        }
      }, this.config.awayDelay);
      
    } else {
      // Page is visible again
      if (this.awayTimeout) {
        clearTimeout(this.awayTimeout);
        this.awayTimeout = null;
      }
      
      if (this.userId) {
        await this.setOnlineStatus('online');

      }
    }
  }

  /**
   * Handle page unload
   */
  async handleBeforeUnload() {
    if (this.userId) {
      try {
        // For page unload, just do a direct database update
        // sendBeacon is unreliable with authentication headers
        await this.setOnlineStatus('offline');
      } catch (error) {
        console.error('[PresenceService] Error setting offline status during unload:', error);
        
        // Last resort: try to update with a simpler method
        try {
          const { error: updateError } = await supabase
            .from('user_presence')
            .update({ 
              status: 'offline',
              is_active: false,
              last_seen: new Date().toISOString()
            })
            .eq('user_id', this.userId);
            
          if (updateError) {
            console.error('[PresenceService] Final update attempt failed:', updateError);
          }
        } catch (finalError) {
          console.error('[PresenceService] All offline attempts failed:', finalError);
        }
      }
    }
  }

  /**
   * Handle user activity with debouncing
   */
  handleUserActivity() {
    // Debounce activity updates
    if (this.activityDebounce) {
      clearTimeout(this.activityDebounce);
    }
    
    this.activityDebounce = setTimeout(() => {
      if (!this.userId) return;
      if (this.isOnline) {
        this.updateActivity();
      } else {
        // User was 'away' but is now active again — restore to online
        this.setOnlineStatus('online');
      }
    }, this.config.activityDebounceDelay);
  }

  /**
   * Update local cache
   * @param {string} userId - User ID
   * @param {string} status - Presence status
   * @param {Object} presenceData - Full presence data
   */
  updateLocalCache(userId, status, presenceData = null) {
    this.presenceCache.set(userId, {
      data: { ...presenceData, status },
      timestamp: Date.now()
    });
  }

  /**
   * Manually set user status (for user-initiated changes)
   * @param {string} status - Status to set
   * @returns {Promise<Object>} - Result object
   */
  async setUserStatus(status) {
    if (!['online', 'away', 'busy', 'offline'].includes(status)) {
      return { success: false, error: 'Invalid status' };
    }
    
    return await this.setOnlineStatus(status);
  }

  /**
   * Get cache statistics for monitoring
   * @returns {Object} - Cache and service statistics
   */
  getCacheStats() {
    return {
      cacheSize: this.presenceCache.size,
      isOnline: this.isOnline,
      userId: this.userId,
      sessionId: this.sessionId,
      hasHeartbeat: !!this.heartbeatInterval,
      hasSubscription: !!this.presenceSubscription,
      config: this.config
    };
  }

  /**
   * Clean up and go offline
   */
  async destroy() {

    
    try {
      // Clear all timers
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }

      if (this.awayTimeout) {
        clearTimeout(this.awayTimeout);
        this.awayTimeout = null;
      }

      if (this.activityDebounce) {
        clearTimeout(this.activityDebounce);
        this.activityDebounce = null;
      }

      // Unsubscribe from real-time
      if (this.presenceSubscription) {
        supabase.removeChannel(this.presenceSubscription);
        this.presenceSubscription = null;
      }

      // Remove event listeners
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      activityEvents.forEach(event => {
        document.removeEventListener(event, this.handleUserActivity);
      });

      // Set offline status
      if (this.userId) {
        await this.setOnlineStatus('offline');
      }

      // Clear cache and state
      this.presenceCache.clear();
      this.userId = null;
      this.isOnline = false;
      

      
    } catch (error) {
      console.error('[PresenceService] Error during cleanup:', error);
    }
  }

  /**
   * Check if service is properly initialized
   * @returns {boolean} - Initialization status
   */
  isInitialized() {
    return !!(this.userId && this.heartbeatInterval && this.presenceSubscription);
  }

  /**
   * Debug user presence status
   * @param {string} userId - User ID to debug
   * @returns {Promise<Object>} - Debug information
   */
  async debugPresenceStatus(userId) {
    // Debug function - only log in development
    if (process.env.NODE_ENV === 'development') {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[PresenceService] Debugging presence for user: ${userId}`);
      }
    }
    
    try {
      // Get current presence from database
      const { data: dbPresence, error } = await supabase
        .from('user_presence')
        .select('user_id,status,last_seen,last_activity,is_active,device_info,session_id')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // Get cached presence
      const cached = this.presenceCache.get(userId);
      
      // Calculate actual status
      const calculatedStatus = dbPresence ? this.calculateActualStatus(dbPresence) : 'offline';
      
      const debugInfo = {
        userId,
        database: dbPresence || 'No record found',
        cached: cached ? {
          data: cached.data,
          timestamp: new Date(cached.timestamp).toISOString(),
          age: Date.now() - cached.timestamp
        } : 'No cache entry',
        calculatedStatus,
        localStatus: this.localStatus[userId] || 'Unknown',
        currentTime: new Date().toISOString()
      };

      // Only log debug info in development
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[PresenceService] Debug info:', debugInfo);
        }
      }
      return debugInfo;
      
    } catch (error) {
      console.error('[PresenceService] Debug error:', error);
      return { error: error.message };
    }
  }

  /**
   * Force refresh presence for a user
   * @param {string} userId - User ID to refresh
   * @returns {Promise<Object>} - Refresh result
   */
  async forceRefreshPresence(userId) {
    // Only log force refresh in development
    if (process.env.NODE_ENV === 'development') {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[PresenceService] Force refreshing presence for user: ${userId}`);
      }
    }
    
    try {
      // Clear cache for this user
      this.presenceCache.delete(userId);
      
      // Get fresh presence from database
      const result = await this.getUserPresence(userId);
      
      if (result.success) {
        // Emit presence change event to update UI
        if (typeof window !== 'undefined' && window.CustomEvent) {
          window.dispatchEvent(new window.CustomEvent('presence-changed', {
            detail: {
              userId,
              status: result.presence.status,
              presence: result.presence,
              eventType: 'REFRESH'
            }
          }));
        }
        
        // Only log completion in development
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[PresenceService] Force refresh completed for user: ${userId}, status: ${result.presence.status}`);
        }
      }
      
      return result;
      
    } catch (error) {
      console.error('[PresenceService] Force refresh error:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const presenceService = new PresenceService();
export default presenceService;

// Browser console debugging tool
if (typeof window !== 'undefined') {
  window.debugPresence = {
    /**
     * Get detailed presence info for a user
     * Usage: debugPresence.getUser('user-uuid')
     */
    async getUser(userId) {
      if (!userId) {
        console.error('Usage: debugPresence.getUser("user-uuid")');
        return;
      }
      
      console.log(`🔍 Debugging presence for user: ${userId}`);
      const result = await presenceService.debugPresenceStatus(userId);
      console.table(result);
      return result;
    },

    /**
     * Force refresh presence for a user
     * Usage: debugPresence.refresh('user-uuid')
     */
    async refresh(userId) {
      if (!userId) {
        console.error('Usage: debugPresence.refresh("user-uuid")');
        return;
      }
      
      console.log(`🔄 Force refreshing presence for user: ${userId}`);
      const result = await presenceService.forceRefreshPresence(userId);
      console.log('Refresh result:', result);
      return result;
    },

    /**
     * View current cache contents
     * Usage: debugPresence.cache()
     */
    cache() {
      console.log('📦 Current presence cache:');
      const cacheEntries = {};
      presenceService.presenceCache.forEach((value, key) => {
        cacheEntries[key] = {
          ...value.data,
          cached_at: new Date(value.timestamp).toISOString(),
          age_ms: Date.now() - value.timestamp
        };
      });
      console.table(cacheEntries);
      return cacheEntries;
    },

    /**
     * Clear cache for specific user or all users
     * Usage: debugPresence.clearCache() or debugPresence.clearCache('user-uuid')
     */
    clearCache(userId) {
      if (userId) {
        presenceService.presenceCache.delete(userId);
        console.log(`🗑️ Cleared cache for user: ${userId}`);
      } else {
        presenceService.presenceCache.clear();
        console.log('🗑️ Cleared all presence cache');
      }
    },

    /**
     * Monitor real-time presence events
     * Usage: debugPresence.monitor() - then debugPresence.stopMonitor() to stop
     */
    monitor() {
      if (this._monitoring) {
        console.log('Already monitoring. Use debugPresence.stopMonitor() to stop.');
        return;
      }

      console.log('👁️ Starting presence monitoring...');
      this._monitoring = true;
      
      this._eventListener = (event) => {
        console.log('🔔 Presence event received:', {
          userId: event.detail.userId,
          status: event.detail.status,
          eventType: event.detail.eventType,
          timestamp: new Date().toISOString()
        });
      };
      
      window.addEventListener('presence-changed', this._eventListener);
      console.log('Monitoring active. Check console for real-time events.');
    },

    /**
     * Stop monitoring real-time presence events
     */
    stopMonitor() {
      if (this._eventListener) {
        window.removeEventListener('presence-changed', this._eventListener);
        this._eventListener = null;
        this._monitoring = false;
        console.log('🛑 Stopped presence monitoring');
      }
    },

    /**
     * Get service status
     * Usage: debugPresence.status()
     */
    status() {
      const status = {
        initialized: presenceService.isInitialized(),
        userId: presenceService.userId,
        isOnline: presenceService.isOnline,
        lastActivity: presenceService.lastActivityTime ? new Date(presenceService.lastActivityTime).toISOString() : null,
        cacheSize: presenceService.presenceCache.size,
        hasSubscription: !!presenceService.presenceSubscription,
        hasHeartbeat: !!presenceService.heartbeatInterval
      };
      
      console.log('⚡ Presence Service Status:');
      console.table(status);
      return status;
    },

    /**
     * Show help
     */
    help() {
      console.log(`
🚀 Presence Service Debug Tool

Available commands:
  debugPresence.getUser('user-uuid')     - Get detailed presence info for a user
  debugPresence.refresh('user-uuid')     - Force refresh presence for a user
  debugPresence.cache()                  - View current cache contents
  debugPresence.clearCache()             - Clear all cache or specific user
  debugPresence.monitor()                - Start monitoring real-time events
  debugPresence.stopMonitor()            - Stop monitoring
  debugPresence.status()                 - Show service status
  debugPresence.help()                   - Show this help

Examples:
  debugPresence.getUser('123e4567-e89b-12d3-a456-426614174000')
  debugPresence.refresh('123e4567-e89b-12d3-a456-426614174000')
  debugPresence.monitor()
      `);
    }
  };

  // Show help on load
  console.log('🚀 Presence Service Debug Tool loaded! Type debugPresence.help() for usage.');
}
